"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleQuestionMark as HelpCircle, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
const faqItems = [{
  id: "item-1",
  question: "How quickly can I expect to see SEO results?",
  answer: "Fractional SEO provides faster time-to-impact than traditional agencies. Quick wins from technical fixes and local SEO typically show movement within 4–12 weeks. Meaningful organic growth from content strategy, topical authority, and link building compounds steadily over 6–12 months. I set clear baseline metrics from day one so our progress remains transparent and measurable throughout."
}, {
  id: "item-2",
  question: "How does fractional SEO compare to hiring an agency?",
  answer: "Agencies are structured for broad execution capacity across multi-layered teams, with annual retainers typically ranging from £72,000–£120,000 to cover full-service agency overhead. Fractional SEO, by contrast, focuses directly on senior strategic leadership and embedded advisory. You work directly with an experienced consultant who integrates alongside your leadership and in-house team to set roadmap direction, prioritize technical execution, and maintain commercial accountability—with fractional engagements typically structured between £36,000–£96,000 per year."
}, {
  id: "item-3",
  question: "What's the minimum commitment for fractional SEO services?",
  answer: "I prioritize flexibility without locking you into rigid long-term contracts. A minimum 3-month engagement is recommended to establish comprehensive baselines and demonstrate measurable outcomes. Some clients prefer to start with a focused 90-day diagnostic sprint first before expanding into ongoing strategic advisory."
}, {
  id: "item-4",
  question: "Do you work with businesses outside the UK?",
  answer: "My primary focus is on UK-based businesses, leveraging my deep understanding of British market dynamics, local search behaviour, and regional competitive landscapes. However, I am open to select international opportunities, particularly within English-language markets where international SEO and multi-region technical architecture are required."
}, {
  id: "item-5",
  question: "How do you measure the success of fractional SEO work?",
  answer: "I align SEO outcomes directly with bottom-line commercial metrics: qualified leads, conversion rates, organic revenue pipeline contribution, and customer acquisition costs. Rankings and organic traffic are monitored as leading indicators, but they are never the final goal. Regular, transparent reporting ensures every pound you invest connects directly to business growth."
}, {
  id: "item-6",
  question: "What's the difference between a fractional SEO consultant and an SEO freelancer?",
  answer: "A traditional freelancer usually handles isolated, tactical tasks such as writing a one-off content piece, running a basic audit, or providing keyword lists. As a fractional SEO consultant, I provide ongoing strategic leadership, roadmapping, prioritisation, team mentoring, and personal accountability for commercial outcomes. You get a seasoned in-house SEO director without the full-time executive overhead."
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const [activeItem, setActiveItem] = useState<string>("item-1");

  // FAQ Schema JSON-LD for rich snippet compliance
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map(item => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer
      }
    }))
  };
  return <section id="faq" data-nav="light" className="relative bg-background py-20 lg:py-28 text-foreground">
      {/* FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Section Header */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center max-w-2xl mx-auto mb-14 lg:mb-18">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <HelpCircle className="size-3.5 text-primary" />
            <span>Direct answers</span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Everything you need to know about working with an independent, fractional SEO consultant.
          </p>
        </motion.div>

        {/* Accordion Container */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: "easeOut"
      }} className="bg-card border border-border rounded-2xl p-4 sm:p-8 lg:p-10 shadow-sm">
          <Accordion type="single" collapsible value={activeItem} onValueChange={setActiveItem} className="w-full space-y-3">
            {faqItems.map((item, index) => <div data-index={index} key={item.id}>
                <AccordionItem value={item.id} className="border border-border/80 rounded-xl px-5 sm:px-6 bg-background transition-colors data-[state=open]:border-primary/40 data-[state=open]:shadow-2xs">
                  <AccordionTrigger className="text-base sm:text-lg font-medium text-foreground hover:no-underline py-5 text-left group">
                    <span className="flex items-start gap-3.5 pr-2">
                      <span className="text-xs font-mono font-semibold text-muted-foreground group-data-[state=open]:text-primary pt-1">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="group-data-[state=open]:text-primary transition-colors">
                        {item.question}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground font-sans text-sm sm:text-base leading-relaxed pb-6 pl-8 sm:pl-9 pr-2">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              </div>)}
          </Accordion>
        </motion.div>

        {/* Bottom CTA banner */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-12 text-center flex flex-col sm:flex-row items-center justify-between gap-6 p-6 sm:p-8 rounded-2xl bg-secondary/50 border border-border">
          <div className="text-left">
            <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-1">
              <Sparkles className="size-4" />
              <span>Have a unique SEO challenge?</span>
            </div>
            <p className="text-foreground font-medium text-base sm:text-lg">
              Talk directly with me to audit your current organic search standing.
            </p>
          </div>

          <div className="shrink-0 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 py-5 rounded-lg shadow-sm">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}